import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon, EyeIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import apiClient from '../utils/apiClient';
import { getDocumentUrl, shouldUseImageUrls } from '../utils/imageUtils';

interface Document {
  docId: number;
  filename: string;
  created_at: string;
}

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: Document[];
  category: 'insurance' | 'tollwut';
  categoryId: number;
  dogId: number;
  dogName: string;
  onDocumentsChange: () => void;
  onDocumentCountChange?: (newCount: number) => void;
}

interface ConfirmModalState {
  isOpen: boolean;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant?: 'single' | 'two';
  onConfirm?: () => void;
}

// PDF.js Worker-Setup
const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  documents,
  category,
  categoryId,
  dogId,
  dogName,
  onDocumentsChange,
  onDocumentCountChange
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({ isOpen: false, title: '', message: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [localDocuments, setLocalDocuments] = useState<Document[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => { document.body.style.overflow = ''; clearTimeout(timer); };
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // PDF.js Worker konfigurieren
  useEffect(() => {
    GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
  }, []);


  // Helper function to check if file is PDF
  const isPdfFile = (filename: string) => {
    return filename.toLowerCase().includes('.pdf');
  };

  // DEBUG: Log beim Öffnen des Modals - NUR wenn Modal neu geöffnet wird
  useEffect(() => {
    if (isOpen && localDocuments.length === 0) {
      // Nur beim ersten Öffnen oder wenn keine lokalen Dokumente vorhanden
      console.log('=== MODAL ERSTMALIG GEÖFFNET ===');
      console.log(`Kategorie: ${category}`);
      console.log(`Hund: ${dogName} (ID: ${dogId})`);
      console.log('Initial Documents:', documents);
      
      // Filtere Placeholder-Dokumente raus (temporäre IDs oder "uploading-" Dateinamen)
      const realDocuments = documents.filter(doc => 
        doc.docId < 1000000000000 && // Echte IDs sind kleiner
        !doc.filename.includes('uploading-') && 
        !doc.filename.includes('placeholder-')
      );
      
      console.log('Gefilterte echte Dokumente:', realDocuments);
      setLocalDocuments([...realDocuments]);
      console.log('LocalDocuments gesetzt:', [...realDocuments]);
    } else if (isOpen) {
      console.log('=== MODAL WIEDER GEÖFFNET ===');
      console.log('Behalte bestehende LocalDocuments:', localDocuments);
      console.log('Ignoriere Parent Documents:', documents);
    }
  }, [isOpen]);

  // Reset currentIndex beim Öffnen
  useEffect(() => {
    if (isOpen) {
      console.log(`Setze currentIndex auf 0, Dokumente vorhanden: ${localDocuments.length}`);
      setCurrentIndex(0);
    }
  }, [isOpen]);

  // Dokument laden basierend auf currentIndex und localDocuments - NUR wenn Modal offen ist
  useEffect(() => {
    // NUR ausführen wenn Modal offen ist
    if (!isOpen) {
      console.log('-> Modal geschlossen, überspringe Dokumentenladen');
      return;
    }
    
    console.log(`=== DOKUMENT LADEN ===`);
    console.log(`CurrentIndex: ${currentIndex}`);
    console.log('LocalDocuments:', localDocuments);
    
    if (localDocuments.length > 0 && localDocuments[currentIndex]) {
      const currentDoc = localDocuments[currentIndex];
      console.log(`Lade Dokument:`, currentDoc);
      
      // Starte Übergangsanimation
      setIsTransitioning(true);
      
      if (isPdfFile(currentDoc.filename)) {
        console.log(`-> PDF wird geladen (ID: ${currentDoc.docId})`);
        loadPdfDocument(currentDoc.docId);
        // Reset image state
        setImageUrl(null);
        setImageError(null);
        setLoadingImage(false);
      } else if (isImageFile(currentDoc.filename)) {
        console.log(`-> Bild wird geladen (ID: ${currentDoc.docId})`);
        loadImageDocument(currentDoc.docId);
        // Reset PDF state
        setDocumentUrl(null);
        setPdfError(null);
        setLoadingPdf(false);
      } else {
        console.log(`-> Unsupported file type: ${currentDoc.filename}`);
        // Reset both states for unsupported files
        setDocumentUrl(null);
        setPdfError(null);
        setLoadingPdf(false);
        setImageUrl(null);
        setImageError(null);
        setLoadingImage(false);
        setIsTransitioning(false);
      }
    } else if (localDocuments.length === 0) {
      console.log('-> Keine Dokumente vorhanden');
      // Reset all states only if no documents
      setDocumentUrl(null);
      setPdfError(null);
      setLoadingPdf(false);
      setImageUrl(null);
      setImageError(null);
      setLoadingImage(false);
      setIsTransitioning(false);
    } else {
      console.log(`-> Index ${currentIndex} außerhalb des Bereichs (${localDocuments.length} Dokumente)`);
      // Reset all states
      setDocumentUrl(null);
      setPdfError(null);
      setLoadingPdf(false);
      setImageUrl(null);
      setImageError(null);
      setLoadingImage(false);
      setIsTransitioning(false);
    }
  }, [isOpen, currentIndex, localDocuments]);

  const loadImageDocument = async (docId: number) => {
    try {
      setLoadingImage(true);
      setImageError(null);
      
      // Cleanup old blob URL
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
      
      const response = await apiClient.get(`/api/members/documents/download/${docId}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'image/*'
        }
      });
      
      const blob = response.data;
      
      // Überprüfe ob die Antwort wirklich ein Bild ist
      if (blob.size === 0) {
        throw new Error('Die Datei ist leer');
      }
      
      const mimeType = response.headers['content-type'] || 'image/jpeg';
      
      // Versuche festzustellen, ob es wirklich ein Bild ist
      if (!mimeType.includes('image') && !mimeType.includes('application/octet-stream')) {
        throw new Error(`Dateityp nicht unterstützt: ${mimeType}`);
      }
      
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
      setImageUrl(blobUrl);
      setIsTransitioning(false);
    } catch (err: any) {
      // console.error('Error loading image document:', err);
      let errorMsg = 'Unbekannter Fehler';
      
      if (err.response?.status === 404) {
        errorMsg = 'Dokument nicht gefunden';
      } else if (err.response?.status === 403) {
        errorMsg = 'Keine Berechtigung zum Zugriff auf das Dokument';
      } else if (err.response?.status >= 500) {
        errorMsg = 'Serverfehler beim Laden des Dokuments';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.response?.status) {
        errorMsg = `HTTP-Fehler ${err.response.status}`;
      }
      
      setImageError(`Bild-Vorschau konnte nicht geladen werden: ${errorMsg}`);
      setIsTransitioning(false);
    } finally {
      setLoadingImage(false);
    }
  };

  const loadPdfDocument = async (docId: number) => {
    try {
      setLoadingPdf(true);
      setPdfError(null);
      
      // Cleanup old blob URL
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
      
      const response = await apiClient.get(`/api/members/documents/download/${docId}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf, image/*'
        }
      });
      
      const blob = response.data;
      
      // Überprüfe ob die Antwort wirklich ein PDF ist
      if (blob.size === 0) {
        throw new Error('Die Datei ist leer');
      }
      
      const mimeType = response.headers['content-type'] || 'application/pdf';
      
      // Versuche festzustellen, ob es wirklich ein PDF ist
      if (!mimeType.includes('pdf') && !mimeType.includes('application/octet-stream')) {
        throw new Error(`Dateityp nicht unterstützt: ${mimeType}`);
      }
      
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setDocumentUrl(blobUrl);
      setIsTransitioning(false);
    } catch (err: any) {
      // console.error('Error loading PDF document:', err);
      let errorMsg = 'Unbekannter Fehler';
      
      if (err.response?.status === 404) {
        errorMsg = 'Dokument nicht gefunden';
      } else if (err.response?.status === 403) {
        errorMsg = 'Keine Berechtigung zum Zugriff auf das Dokument';
      } else if (err.response?.status >= 500) {
        errorMsg = 'Serverfehler beim Laden des Dokuments';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.response?.status) {
        errorMsg = `HTTP-Fehler ${err.response.status}`;
      }
      
      setPdfError(`PDF-Vorschau konnte nicht geladen werden: ${errorMsg}`);
      setIsTransitioning(false);
    } finally {
      setLoadingPdf(false);
    }
  };

  // Cleanup document URLs when component unmounts
  useEffect(() => {
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [documentUrl, imageUrl]);


  // Reset localDocuments beim Schließen
  useEffect(() => {
    if (!isOpen && localDocuments.length > 0) {
      console.log('=== MODAL GESCHLOSSEN - RESET ===');
      // Reset alle States
      setLocalDocuments([]);
      setCurrentIndex(0);
      setDocumentUrl(null);
      setPdfError(null);
      setLoadingPdf(false);
      setImageUrl(null);
      setImageError(null);
      setLoadingImage(false);
      setIsTransitioning(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentDocument = localDocuments[currentIndex];
  const categoryName = category === 'insurance' ? 'Haftpflichtversicherung' : 'Tollwutimpfung';

  const handlePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : localDocuments.length - 1;
    console.log(`=== BLÄTTERN RÜCKWÄRTS ===`);
    console.log(`Von Index ${currentIndex} zu ${newIndex}`);
    console.log('Aktuelle Dokumente:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < localDocuments.length - 1 ? currentIndex + 1 : 0;
    console.log(`=== BLÄTTERN VORWÄRTS ===`);
    console.log(`Von Index ${currentIndex} zu ${newIndex}`);
    console.log('Aktuelle Dokumente:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
    setCurrentIndex(newIndex);
  };

  const handleDeleteDocument = async (docId: number) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Dokument löschen',
      message: `Möchten Sie dieses ${categoryName}-Dokument wirklich löschen?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          setIsDeleting(true);
          const response = await apiClient.delete(`/api/dogs/delete_dog_document/${dogId}/${docId}`);

          if (response.data.success) {
            console.log(`=== DOKUMENT LÖSCHEN ===`);
            console.log(`Gelöschtes Dokument ID: ${docId}`);
            console.log('Dokumente VORHER:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
            
            // STOPPE alle laufenden Ladeprozesse SOFORT
            setLoadingPdf(false);
            setLoadingImage(false);
            setIsTransitioning(false);
            
            // Reset alle States vor der Dokumentenaktualisierung
            setDocumentUrl(null);
            setPdfError(null);
            setImageUrl(null);
            setImageError(null);
            
            // Entferne das Dokument aus lokalDocuments
            const updatedDocuments = localDocuments.filter(doc => doc.docId !== docId);
            console.log('Dokumente NACHHER:', updatedDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
            console.log(`Dokumentenanzahl: ${localDocuments.length} -> ${updatedDocuments.length}`);
            
            // Informiere SOFORT über neue Dokumentenanzahl (BEVOR State-Updates)
            if (onDocumentCountChange) {
              console.log(`-> onDocumentCountChange aufgerufen mit: ${updatedDocuments.length}`);
              onDocumentCountChange(updatedDocuments.length);
            }
            
            // Aktualisiere lokale Dokumente
            setLocalDocuments(updatedDocuments);
            
            // Intelligente Navigation nach dem Löschen
            if (updatedDocuments.length === 0) {
              console.log('-> Letztes Dokument gelöscht, setze Index auf 0');
              setCurrentIndex(0);
            } else {
              let newIndex = currentIndex;
              
              // Wenn wir das letzte Dokument gelöscht haben, gehe zum vorigen
              if (currentIndex >= updatedDocuments.length) {
                newIndex = updatedDocuments.length - 1;
                console.log(`-> Index angepasst: ${currentIndex} -> ${newIndex} (letztes Dokument gelöscht)`);
              } else {
                console.log(`-> Index bleibt bei ${currentIndex} (nächstes Dokument wird angezeigt)`);
              }
              
              setCurrentIndex(newIndex);
            }
            
            // Erfolgs-Modal
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Erfolgreich',
              message: 'Dokument wurde erfolgreich gelöscht.',
              variant: 'single'
            });
            
            console.log('=== LÖSCHEN ABGESCHLOSSEN ===');
            // Rufe onDocumentsChange auf, um echte Daten vom Server zu holen
            if (onDocumentsChange) {
              onDocumentsChange();
            }
          } else {
            throw new Error('Fehler beim Löschen');
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Das Dokument konnte nicht gelöscht werden.',
            variant: 'single'
          });
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('document', file);
      formData.append('category_id', categoryId.toString());
      formData.append('description', `${categoryName} - ${file.name}`);

      const response = await apiClient.post(`/api/dogs/upload_dog_document/${dogId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        console.log(`=== DOKUMENT HOCHLADEN ===`);
        console.log(`Hochgeladene Datei: ${file.name}`);
        console.log('Dokumente VORHER:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
        
        // Erstelle ein neues Dokument-Objekt (mit der ID aus der API-Antwort)
        const newDocument: Document = {
          docId: response.data.id,
          filename: `${categoryId === 1 ? 'tollwut' : 'insurance'}/${file.name}`,
          created_at: new Date().toISOString()
        };
        
        // Füge das neue Dokument zu localDocuments hinzu
        const updatedDocuments = [...localDocuments, newDocument];
        console.log('Neues Dokument:', newDocument);
        console.log('Dokumente NACHHER:', updatedDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
        console.log(`Dokumentenanzahl: ${localDocuments.length} -> ${updatedDocuments.length}`);
        
        // Aktualisiere lokale Dokumente
        setLocalDocuments(updatedDocuments);
        
        // Informiere über neue Dokumentenanzahl SOFORT
        if (onDocumentCountChange) {
          console.log(`-> onDocumentCountChange aufgerufen mit: ${updatedDocuments.length}`);
          onDocumentCountChange(updatedDocuments.length);
        }
        
        // Springe zum neu hochgeladenen Dokument
        const newDocumentIndex = updatedDocuments.length - 1;
        console.log(`-> Springe zu neuem Dokument (Index: ${newDocumentIndex})`);
        setCurrentIndex(newDocumentIndex);
        
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Dokument wurde erfolgreich hochgeladen.',
          variant: 'single'
        });
        
        console.log('=== HOCHLADEN ABGESCHLOSSEN ===');
        // Rufe onDocumentsChange auf, um echte Daten vom Server zu holen
        if (onDocumentsChange) {
          onDocumentsChange();
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Das Dokument konnte nicht hochgeladen werden.',
        variant: 'single'
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const isImageFile = (filename: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().includes(ext));
  };


  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
        onClick={onClose}
      >
        <div
          className="w-full sm:max-w-4xl bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
          onClick={(e) => e.stopPropagation()}
          style={{ height: '90vh' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
              <EyeIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
                {categoryName} - {dogName}
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {localDocuments.length > 0 ? `${currentIndex + 1} von ${localDocuments.length} Dokumenten` : 'Keine Dokumente'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Document View */}
          <div className="flex-1 flex flex-col min-h-0">
            {localDocuments.length > 0 && currentDocument ? (
              <>
                {/* Navigation */}
                {localDocuments.length > 1 && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
                    <button
                      onClick={handlePrevious}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                      Vorheriges
                    </button>
                    
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Hochgeladen: {new Date(currentDocument.created_at).toLocaleDateString('de-DE')}
                    </span>
                    
                    <button
                      onClick={handleNext}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Nächstes
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {/* Document Content */}
                <div className="flex-1 p-4 flex items-center justify-center bg-gray-50 dark:bg-gray-800 overflow-auto relative">
                  {/* Loading Overlay */}
                  {isTransitioning && (
                    <div className="absolute inset-0 bg-gray-50 dark:bg-gray-800 flex items-center justify-center z-10">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Dokument wird geladen...</span>
                      </div>
                    </div>
                  )}
                  
                  {isImageFile(currentDocument.filename) ? (
                    <>
                      {loadingImage ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                          <p className="ml-3 text-gray-600 dark:text-gray-400">Bild wird geladen...</p>
                        </div>
                      ) : imageError ? (
                        <div className="text-center">
                          <div className="mb-4">
                            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Bild nicht verfügbar
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {imageError}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Datei: {currentDocument.filename.split('/').pop()}
                          </p>
                        </div>
                      ) : imageUrl ? (
                        <img
                          src={imageUrl}
                          alt="Dokument"
                          className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                        />
                      ) : (
                        <div className="text-center">
                          <div className="mb-4">
                            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Bild nicht verfügbar
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Das Bild konnte nicht geladen werden.
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Datei: {currentDocument.filename.split('/').pop()}
                          </p>
                        </div>
                      )}
                    </>
                  ) : isPdfFile(currentDocument.filename) ? (
                    <div className="w-full h-full flex flex-col">
                      {pdfError ? (
                        <div className="flex-1 flex items-center justify-center text-center">
                          <div>
                            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                              PDF nicht verfügbar
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                              {pdfError}
                            </p>
                          </div>
                        </div>
                      ) : loadingPdf ? (
                        <div className="flex-1 flex items-center justify-center">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 dark:text-gray-400">PDF wird geladen...</p>
                          </div>
                        </div>
                      ) : documentUrl ? (
                        <div className="w-full h-full bg-gray-200 dark:bg-gray-700">
                          <Worker workerUrl={PDFJS_WORKER_URL}>
                            <div 
                              className="w-full h-full overflow-auto"
                              style={{ 
                                height: '60vh', 
                                minHeight: '500px',
                                maxHeight: '75vh',
                                border: '1px solid #e5e7eb'
                              }}
                            >
                              <Viewer
                                fileUrl={documentUrl}
                                defaultScale={SpecialZoomLevel.PageWidth}
                              />
                            </div>
                          </Worker>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-center">
                          <div>
                            <EyeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                              PDF wird vorbereitet...
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="mb-4">
                        <EyeIcon className="h-16 w-16 text-gray-400 mx-auto" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Dokument-Vorschau nicht verfügbar
                      </p>
                      <a
                        href={shouldUseImageUrls() ? getDocumentUrl(currentDocument.docId) : ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <EyeIcon className="h-5 w-5" />
                        Dokument öffnen
                      </a>
                    </div>
                  )}
                  
                  {/* Fallback for broken images */}
                  <div className="hidden text-center">
                    <div className="mb-4">
                      <EyeIcon className="h-16 w-16 text-gray-400 mx-auto" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Bild konnte nicht geladen werden
                    </p>
                    <a
                      href={shouldUseImageUrls() ? getDocumentUrl(currentDocument.docId) : ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <EyeIcon className="h-5 w-5" />
                      Dokument öffnen
                    </a>
                  </div>
                </div>

                {/* Document Actions */}
                <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {currentDocument.filename.split('/').pop()}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Hochgeladen: {new Date(currentDocument.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument(currentDocument.docId)}
                    disabled={isDeleting}
                    className="flex items-center gap-2 px-3 py-2 bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-400 rounded-lg hover:bg-error-200 dark:hover:bg-error-900/30 transition-colors disabled:opacity-50"
                  >
                    {isDeleting ? (
                      <div className="w-4 h-4 border-2 border-error-700 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                    Löschen
                  </button>
                </div>
              </>
            ) : (
              /* No Documents */
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  <EyeIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Keine Dokumente vorhanden
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Es wurden noch keine {categoryName}-Dokumente für {dogName} hochgeladen.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Upload Section */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors cursor-pointer disabled:opacity-50">
                {isUploading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PlusIcon className="h-5 w-5" />
                )}
                {isUploading ? 'Hochladen...' : 'Dokument hinzufügen'}
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                PDF, JPG, PNG, GIF (max. 10MB)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ConfirmationModal - Portal-Style mit höherem z-index */}
      {confirmModal.isOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => {
              if (confirmModal.variant === 'single') {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
              } else if (confirmModal.variant === 'two') {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
              }
            }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90] animate-fadeIn"
          />

          {/* Modal - über DocumentPreviewModal */}
          <div className="fixed inset-0 flex items-center justify-center z-[90] p-4 pointer-events-none">
            <div className={`bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full pointer-events-auto relative ${
              confirmModal.variant === 'single' ? 'max-w-sm' : 'max-w-md'
            } overflow-hidden animate-scaleIn`}>
              
              {/* Content */}
              <div className={confirmModal.variant === 'single' ? 'p-8' : 'p-8 pb-6'}>
                {/* Icon Container */}
                <div className={`${
                  confirmModal.variant === 'single' ? 'w-20 h-20' : 'w-16 h-16'
                } mx-auto mb-5 ${
                  confirmModal.type === 'success' ? 'bg-primary-100 dark:bg-primary-900/20' :
                  confirmModal.type === 'warning' ? 'bg-warning-100 dark:bg-warning-900/20' :
                  confirmModal.type === 'error' ? 'bg-error-100 dark:bg-error-900/20' :
                  confirmModal.type === 'question' ? 'bg-primary-100 dark:bg-primary-900/20' :
                  'bg-info-100 dark:bg-info-900/20'
                } rounded-full flex items-center justify-center`}>
                  {confirmModal.type === 'success' && <CheckCircleIcon className={`text-primary-600 ${confirmModal.variant === 'single' ? 'h-10 w-10' : 'h-8 w-8'}`} />}
                  {confirmModal.type === 'warning' && <ExclamationTriangleIcon className={`text-warning-600 ${confirmModal.variant === 'single' ? 'h-10 w-10' : 'h-8 w-8'}`} />}
                  {confirmModal.type === 'error' && <ExclamationTriangleIcon className={`text-error-600 ${confirmModal.variant === 'single' ? 'h-10 w-10' : 'h-8 w-8'}`} />}
                  {confirmModal.type === 'question' && <ExclamationTriangleIcon className={`text-primary-600 ${confirmModal.variant === 'single' ? 'h-10 w-10' : 'h-8 w-8'}`} />}
                  {(!confirmModal.type || confirmModal.type === 'info') && <ExclamationTriangleIcon className={`text-info-600 ${confirmModal.variant === 'single' ? 'h-10 w-10' : 'h-8 w-8'}`} />}
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-3">
                  {confirmModal.title}
                </h3>
                
                {/* Message */}
                <div className="text-base text-gray-600 dark:text-gray-300 leading-relaxed px-2 text-center">
                  {confirmModal.message}
                </div>
              </div>

              {/* Buttons */}
              {confirmModal.variant === 'single' ? (
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                  className={`w-full py-5 ${
                    confirmModal.type === 'success' ? 'bg-primary-600 hover:bg-primary-700' :
                    confirmModal.type === 'warning' ? 'bg-warning-600 hover:bg-warning-700' :
                    confirmModal.type === 'error' ? 'bg-error-600 hover:bg-error-700' :
                    confirmModal.type === 'question' ? 'bg-primary-600 hover:bg-primary-700' :
                    'bg-info-600 hover:bg-info-700'
                  } text-white font-semibold transition-colors`}
                >
                  OK
                </button>
              ) : (
                <div className="flex border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                    className="flex-1 py-5 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => { confirmModal.onConfirm?.(); setConfirmModal(prev => ({ ...prev, isOpen: false })); }}
                    className={`flex-1 py-5 ${
                      confirmModal.type === 'success' ? 'bg-primary-600 hover:bg-primary-700' :
                      confirmModal.type === 'warning' ? 'bg-warning-600 hover:bg-warning-700' :
                      confirmModal.type === 'error' ? 'bg-error-600 hover:bg-error-700' :
                      confirmModal.type === 'question' ? 'bg-primary-600 hover:bg-primary-700' :
                      'bg-info-600 hover:bg-info-700'
                    } text-white font-semibold transition-colors`}
                  >
                    Bestätigen
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DocumentPreviewModal;